import { useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { SocketContext } from '@frontend/contexts';

const DEBOUNCE_MS = 400;

/**
 * Realtime board updates. The backend publishes stickyboard:* events to the
 * user's room (sb:{instance}:{user}) when a sticky/section/stack changes —
 * including the Steward's curation writes after auto-curate fires. We debounce
 * a single invalidation of the whole `['stickyboard', …]` query tree so the
 * board re-fetches and re-renders. Mirrors useArtifactSocketInvalidation.
 */
const STICKYBOARD_EVENTS = [
  'stickyboard:sticky:created',
  'stickyboard:sticky:updated',
  'stickyboard:sticky:dismissed',
  'stickyboard:section:created',
  'stickyboard:section:updated',
  'stickyboard:section:deleted',
  'stickyboard:stack:created',
  'stickyboard:stack:updated',
  'stickyboard:stack:deleted',
] as const;

export function useStickyboardSocket() {
  const queryClient = useQueryClient();
  const socketCtx = useContext(SocketContext);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!socketCtx) return;
    const { on, off } = socketCtx;

    const handle = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['stickyboard'] });
      }, DEBOUNCE_MS);
    };

    STICKYBOARD_EVENTS.forEach(event => on(event, handle));
    return () => {
      STICKYBOARD_EVENTS.forEach(event => off(event, handle));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [socketCtx, queryClient]);

  // Join the sb:{instance}:{user} room (re-emitted on each reconnect).
  const connected = socketCtx?.connected ?? false;
  const emit = socketCtx?.emit;
  useEffect(() => {
    if (!connected || !emit) return;
    emit('sb_subscribe');
    return () => emit('sb_unsubscribe');
  }, [connected, emit]);
}
